import { useEffect, useState } from "react";

/**
 * useLockCountdown(slug)
 * Reads any active Rate Lock stored in localStorage for the given corridor
 * slug (key: `ix.rate-lock.<slug>`) and returns a live-ticking string of
 * the remaining time until expiry. Returns `null` when there is no lock
 * or the lock has expired.
 *
 * Ticks every second. Auto-clears expired locks from localStorage.
 */
export function useLockCountdown(slug, labels) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;

    const compute = () => {
      let lock = null;
      try {
        const raw = localStorage.getItem(`ix.rate-lock.${slug}`);
        if (raw) lock = JSON.parse(raw);
      } catch (_) { /* ignore */ }
      if (!lock?.expires_at) {
        if (alive) setRemaining(null);
        return;
      }
      const diff = new Date(lock.expires_at).getTime() - Date.now();
      if (diff <= 0) {
        try { localStorage.removeItem(`ix.rate-lock.${slug}`); } catch (_) { /* ignore */ }
        if (alive) setRemaining(null);
        return;
      }
      const s = Math.floor(diff / 1000);
      const h = Math.floor(s / 3600).toString().padStart(2, "0");
      const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
      const sec = (s % 60).toString().padStart(2, "0");
      const hLbl = labels?.hours || "h";
      const mLbl = labels?.minutes || "m";
      const secLbl = labels?.seconds || "s";
      if (alive) setRemaining(`${h}${hLbl} ${m}${mLbl} ${sec}${secLbl}`);
    };

    compute();
    const id = setInterval(compute, 1000);
    // React to localStorage changes from other tabs / RateLockCard mounts.
    const onStorage = (e) => {
      if (!e.key || e.key === `ix.rate-lock.${slug}`) compute();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("storage", onStorage);
    };
  }, [slug, labels?.hours, labels?.minutes, labels?.seconds]);

  return remaining;
}

/**
 * Small chip that renders a live "Lock expires in HH:MM:SS" pill next to a
 * corridor tile. Returns null (no DOM) when there is no active lock.
 */
export default function LockCountdownChip({ slug, tone = "dark", label, timeLabels, testId }) {
  const remaining = useLockCountdown(slug, timeLabels);
  if (!remaining) return null;
  const cls =
    tone === "dark"
      ? "bg-lime/20 border-lime/50 text-lime"
      : "bg-forest/10 border-forest/30 text-forest";
  return (
    <span
      className={
        "mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono tracking-widest " +
        cls
      }
      data-testid={testId || `lock-countdown-${slug}`}
      title={label}
    >
      <span aria-hidden>🔒</span>
      {remaining}
    </span>
  );
}
